<script setup lang="ts">
import UIBrow from '@/components/ui/UIBrow.vue'
import useLanguage from '@/composables/useLanguage'
import { colors } from '@/utils/colors'
import { DropdownEventKey } from '@/utils/eventBusKeys/dropdownEventKey'
import { ToolTipEventKey } from '@/utils/eventBusKeys/tooltipKey'
import { ITooltipDropdownItem, TTooltipDropdownActiveStyle } from '@lifk/types'
import {
  PhArrowDown,
  PhArrowUp,
  PhCheckCircle,
  PhCircle
} from '@phosphor-icons/vue'
import { useEventBus } from '@vueuse/core'
import { computed, onMounted, ref } from 'vue'

/** DEPRECATED */
const props = withDefaults(
  defineProps<{
    dropdownId: string
    options: ITooltipDropdownItem[]
    activeStyle?: TTooltipDropdownActiveStyle
    dropdownTitle?: string
    showDirectionArrow?: boolean
    showItemBorders?: boolean
  }>(),
  {
    activeStyle: 'default',
    showDirectionArrow: true,
    showItemBorders: false
  }
)

const i18n = useLanguage('UIDropdownMenu')
const toolTipEventBus = useEventBus(ToolTipEventKey)
const dropdownEventBus = useEventBus(DropdownEventKey)
const dropdownOptionsDiv = ref<HTMLElement | undefined>()
const isToggled = ref(false)
const currentSelected = ref<string | null>(props.options[0].option)
const focusedIndex = ref(0)

const optionsElements = computed(() => {
  if (dropdownOptionsDiv.value?.children) {
    return Array.from(dropdownOptionsDiv.value.children).map(
      (el) => el as HTMLElement
    )
  }
  return []
})

const optionSelected = (option: string, key?: string) => {
  if (currentSelected.value === option) {
    isToggled.value = !isToggled.value
  } else {
    isToggled.value = false
    currentSelected.value = option
  }

  dropdownEventBus.emit({ id: props.dropdownId, selected: key ?? option })
  closeDropdown()
}

const activeOptionId = computed(() => {
  // return if index is out of bounds
  if (!props.options[focusedIndex.value]) return undefined
  return `${props.dropdownId}-option-${focusedIndex.value}`
})

const closeDropdown = () => {
  toolTipEventBus.emit({ type: 'hide' })
}

const onKeyDown = (e: KeyboardEvent) => {
  const prevFocus = focusedIndex.value
  let nextFocus = prevFocus

  if (e.key === 'Tab') {
    closeDropdown()
    return
  }

  switch (e.key) {
    case 'ArrowUp':
      e.preventDefault()
      nextFocus = prevFocus - 1
      break
    case 'ArrowDown':
      e.preventDefault()
      if (prevFocus < props.options.length - 1) {
        nextFocus = prevFocus + 1
      }
      break
    case 'Enter':
    case ' ':
      e.preventDefault()
      if (props.options[prevFocus]) {
        optionSelected(
          props.options[prevFocus].option,
          props.options[prevFocus].key
        )
      }
      return
  }

  if (nextFocus < 0) {
    closeDropdown()
  } else if (nextFocus !== prevFocus) {
    focusedIndex.value = nextFocus
  }
}

const onMouseMove = () => {
  if (optionsElements.value && optionsElements.value.length > 0) {
    optionsElements.value.forEach((el) => {
      el.blur()
    })
  }
}

onMounted(() => {
  dropdownOptionsDiv.value?.focus()
  // focusFirstChild(dropdownOptionsDiv.value)
  focusedIndex.value = 0
})
</script>

<template>
  <div
    :id="dropdownId"
    ref="dropdownOptionsDiv"
    role="listbox"
    tabindex="0"
    :aria-activedescendant="activeOptionId"
    :aria-label="i18n.dropdownItems"
    class="py-[10px]"
    @keydown.stop="onKeyDown"
  >
    <UIBrow
      v-if="dropdownTitle"
      :text="dropdownTitle"
      size="sm"
      class="text-default pl-4 py-1"
    />
    <div
      v-for="({ option, key, selected }, index) in options"
      :id="`${dropdownId}-option-${index}`"
      :key="option"
      role="option"
      :aria-selected="selected"
      class="text-body cursor-pointer outline-none"
      :class="[
        selected && activeStyle === 'default'
          ? 'font-medium text-emphasis'
          : 'font-normal',
        {
          'border-b border-b-lightest':
            showItemBorders && index < options.length - 1,
          'px-5': showItemBorders,
          'px-4': !showItemBorders
        },
        { 'flex justify-start items-center': activeStyle === 'checkmark' },
        'py-3',
        'hover:bg-hover'
      ]"
      @click.stop.prevent="() => optionSelected(option, key)"
      @mousemove="onMouseMove"
    >
      <template v-if="activeStyle === 'checkmark'">
        <div class="flex items-center w-full">
          <div class="w-4 mr-3">
            <PhCheckCircle
              v-if="selected"
              :size="16"
              weight="fill"
              :color="`#${colors.slate}`"
            />
            <PhCircle
              v-else
              :size="16"
              weight="bold"
              :color="`#${colors.slate}`"
            />
          </div>
          <span>{{ option }}</span>
          <div
            v-if="selected && dropdownTitle && showDirectionArrow"
            class="flex ml-auto pl-2"
          >
            <component
              :is="
                currentSelected === option && isToggled
                  ? PhArrowUp
                  : PhArrowDown
              "
              :size="16"
              weight="bold"
              :color="`#${colors.slate}`"
            />
          </div>
        </div>
      </template>

      <template v-else>
        <span>{{ option }}</span>
      </template>
    </div>
  </div>
</template>
